'use client';

import { refreshAuth } from '@/api/authRefresh';
import { AuthContext, type AuthContextValue } from '@/contexts/AuthContext';
import { LOGGED_OUT_SNAPSHOT, deriveAuthSnapshot, type AuthSnapshot } from '@/lib/auth';
import { useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';

type AuthStatus = 'checking' | 'authenticated' | 'unauthenticated';

interface AuthProviderProps {
  /**
   * Auth snapshot derived from the request cookies during SSR (see
   * app/layout.tsx). The server HTML and the client's first render both use
   * cookie-derived state, so personalized UI is correct on the very first
   * paint with no flicker and no hydration mismatch.
   *
   * Optional so statically-served shells (no server, no request cookies —
   * e.g. private routes served directly by nginx) keep working: without it,
   * auth is derived from document.cookie on the client.
   */
  initialAuth?: AuthSnapshot;
  children: React.ReactNode;
}

export const AuthProvider = ({ initialAuth, children }: AuthProviderProps) => {
  const [cookies, , removeCookie] = useCookies(['idToken', 'user', 'loginAs', 'loginAsKey']);

  // On the client, react-cookie reads document.cookie synchronously, so the
  // live derivation is already correct on the first render. During server
  // rendering react-cookie has no request context, so use the snapshot the
  // layout derived from the request cookies instead; both come from the same
  // cookies, so server and client output agree.
  const snapshot: AuthSnapshot =
    typeof window === 'undefined'
      ? (initialAuth ?? LOGGED_OUT_SNAPSHOT)
      : deriveAuthSnapshot({
          idToken: cookies.idToken,
          loginAs: cookies.loginAs,
        });

  const [authStatus, setAuthStatus] = useState<AuthStatus>(() =>
    snapshot.isAuthenticated ? 'authenticated' : 'checking',
  );

  useEffect(() => {
    if (snapshot.isAuthenticated) {
      setAuthStatus('authenticated');
      return;
    }

    // Never regress from 'authenticated' to 'checking': a user already on
    // the page keeps seeing it uninterrupted while we retry silently in the
    // background. Only a first-ever check (still 'checking') stays gated.
    setAuthStatus((current) => (current === 'authenticated' ? current : 'checking'));

    // refreshAuth dedupes against any refresh already triggered by a 401'd
    // API call, so both paths share one in-flight request.
    refreshAuth().then((refreshed) => {
      // On success, wait for cookies.idToken itself to reflect the new
      // token (this effect re-runs once it does, via the branch above)
      // rather than trusting the response body alone.
      if (!refreshed) {
        setAuthStatus('unauthenticated');
      }
    });
  }, [cookies.idToken, snapshot.isAuthenticated]);

  const logout = () => {
    if (snapshot.isLoginAs) {
      removeCookie('loginAs', {
        path: '/',
        secure: false,
        httpOnly: false,
      });
      removeCookie('loginAsKey', {
        path: '/',
        secure: false,
        httpOnly: false,
      });
      window.location.reload();
    } else {
      removeCookie('user', {
        path: '/',
        domain: process.env.NEXT_PUBLIC_COOKIE_DOMAIN,
        secure: true,
        httpOnly: false,
      });
      window.location.href = `${process.env.NEXT_PUBLIC_ACCOUNT_CENTER_URL}/api/v1/auth/logout?redirect=${encodeURIComponent(window.location.href)}`;
    }
  };

  const value: AuthContextValue = {
    ...snapshot,
    isAuthenticated: authStatus === 'authenticated',
    isResolvingAuth: authStatus === 'checking',
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
